import React, { useState } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Icon from 'tbk-components/src/components/Icon';
import Image, { IImage } from 'tbk-components/src/components/Image';
import AnimatedArcs from '../../common/AnimatedArcs';
import { useTranslation } from 'react-i18next';

export interface IMediaFileProps extends IImage {
	size?: number;
	prettySize?: string;
	label?: string;
}

export interface IResourceProps {
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Description
	 * @control richtext
	 */
	description?: string;
	/**
	 * Label
	 */
	label?: string;
	/**
	 * Image
	 */
	image?: IImage;
	/**
	 * Item Link
	 *
	 * @control mediafile
	 */
	link?: IMediaFileProps;
	fileLabel?: string;
	/**
	 * Select Filter (E.g. Year)
	 */
	selectFilter?: string;
}

export interface IResourceDownloadProps extends IBaseElementProps {
	/**
	 * Style
	 */
	style?: 'CTA' | 'List';
	/**
	 * Size
	 */
	size?: 'Large' | 'Small';
	/**
	 * Background Colour
	 */
	backgroundColor?: 'Light' | 'Dark';
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Resources
	 */
	resources?: IResourceProps[];
	/**
	 * Filter Select Label
	 */
	filterSelectLabel?: string;
	enableLineAnimations?: boolean;
}

/**
 * Calian Resource Download
 * @icon format-gallery
 * @block
 */
const ResourceDownload: React.FC<IResourceDownloadProps> = ({
	id,
	className,
	classNames = [],
	style = 'CTA',
	size = 'Large',
	backgroundColor = 'Light',
	heading,
	resources,
	filterSelectLabel = 'Year:',
	enableLineAnimations = true,
}) => {
	const { t } = useTranslation();
	let selectFilters = [];
	if (resources) {
		resources.map(({ selectFilter }) => selectFilters.push(selectFilter));
		selectFilters = [...new Set(selectFilters)];
	}
	const [selectFilter, setSelectFilter] = useState<string>('');

	return (
		<div
			id={id}
			className={
				className || cn('calian-resource-download', ...classNames)
			}
		>
			<div className="container-fluid mw-1440px">
				{style === 'List' && heading && (
					<h2 className="d-flex justify-content-center mb-md-9">
						{heading}
					</h2>
				)}
				{style === 'List' && selectFilters.length > 1 && (
					<div className="d-flex justify-content-center align-items-center mb-3 mb-md-6">
						<div className="position-relative">
							<label
								className="calian-resource-download__select-label me-3"
								htmlFor="filter"
							>
								{filterSelectLabel}
							</label>
							<select
								onChange={(e) =>
									setSelectFilter(e.target.value)
								}
								className="calian-resource-download__select border-0 lh-1 pt-4 pe-3 pb-4 ps-4"
								id="filter"
							>
								<option value="">All</option>
								{selectFilters.map((year, index) => (
									<option key={index} value={year}>
										{year}
									</option>
								))}
							</select>
							<div className="calian-resource-download__select-icon position-absolute">
								<Icon name={'caret-right-slim'} />
							</div>
						</div>
					</div>
				)}
				<div
					className={cn(
						'calian-resource-download__container position-relative mx-auto',
						style === 'CTA' &&
							(!backgroundColor || backgroundColor === 'Light') &&
							'calian-resource-download__container--light',
						style === 'CTA' &&
							backgroundColor === 'Dark' &&
							'calian-resource-download__container--dark',
						style === 'CTA' &&
							size === 'Small' &&
							'calian-resource-download__container--small mx-auto',
					)}
				>
					<div
						className={cn(
							'calian-resource-download__wrapper position-relative z-2',
							style === 'CTA' &&
								(!size || size === 'Large') &&
								'p-3 pt-5 px-md-6 py-md-5  p-xl-10',
							style === 'CTA' &&
								size === 'Small' &&
								'p-3 pt-5 p-md-6',
							style === 'List' &&
								'calian-resource-download__wrapper--list',
						)}
					>
						{resources &&
							resources.length > 0 &&
							resources.map(
								(resource, index) =>
									(selectFilter === '' ||
										(selectFilters.length > 0 &&
											selectFilter ===
												resource.selectFilter)) && (
										<div
											key={index}
											className={cn(
												style === 'List' &&
													'calian-resource-download__list-link',
											)}
										>
											{style === 'CTA' && index < 1 && (
												<div
													className={cn(
														'calian-resource-download__tile gap-4',
														style === 'CTA' &&
															(!size ||
																size ===
																	'Large') &&
															'gap-md-9 gap-lg-14',
														style === 'CTA' &&
															size === 'Small' &&
															'gap-md-7',
													)}
												>
													<div className="calian-resource-download__image mx-auto position-relative">
														<div className="position-relative">
															<Image
																{...resource.image}
																className="img-fluid position-relative z-3"
															/>
														</div>
														{size === 'Large' &&
															resource?.link
																?.label && (
																<a
																	href={
																		resource
																			?.link
																			?.src ||
																		'#generated-automatically-for-front-end'
																	}
																	target="_blank"
																	className={cn(
																		'calian-resource-download__link calian-resource-download__link--image z-3 position-absolute',
																		(!backgroundColor ||
																			backgroundColor ===
																				'Light') &&
																			'btn btn-primary',
																		backgroundColor ===
																			'Dark' &&
																			'btn btn-light',
																	)}
																	rel="noreferrer noopener"
																>
																	<span>
																		<Icon
																			name={
																				'download'
																			}
																		/>
																	</span>
																</a>
															)}
													</div>
													<div className="calian-resource-download__content d-flex flex-column justify-content-center align-center">
														{size != 'Small' &&
															resource.heading && (
																<h2 className="mb-0 mb-md-3">
																	<span className="me-1 me-md-2">
																		{
																			resource.heading
																		}
																	</span>
																	{resource.label && (
																		<span className="calian-resource-download__label d-inline-block align-middle">
																			{
																				resource.label
																			}
																		</span>
																	)}
																</h2>
															)}
														{size === 'Small' &&
															resource.heading && (
																<h3 className="mb-0 ps-0">
																	<span className="me-1 me-md-2">
																		{
																			resource.heading
																		}
																	</span>
																	{resource.label && (
																		<span className="calian-resource-download__label d-inline-block align-middle">
																			{
																				resource.label
																			}
																		</span>
																	)}
																</h3>
															)}

														{size != 'Small' &&
															resource.description && (
																<div
																	className="calian-resource-download__description"
																	dangerouslySetInnerHTML={{
																		__html: resource.description,
																	}}
																/>
															)}
														{(resource?.link
															?.label ||
															resource?.link
																?.src) && (
															<div
																className={`${
																	resource.description &&
																	size !==
																		'Small'
																		? 'mt-3 mt-md-6'
																		: 'mt-3'
																}`}
															>
																<a
																	href={
																		resource
																			?.link
																			?.src ||
																		'#generated-automatically-for-front-end'
																	}
																	target="_blank"
																	className={cn(
																		'calian-resource-download__link',
																		(!backgroundColor ||
																			backgroundColor ===
																				'Light') &&
																			'btn btn-primary d-inline-flex align-items-center',
																		backgroundColor ===
																			'Dark' &&
																			'btn btn-secondary d-inline-flex align-items-center',
																		size ===
																			'Small' &&
																			'btn-sm p-5',
																	)}
																	rel="noreferrer noopener"
																>
																	<span>
																		<Icon
																			name={
																				'download'
																			}
																		/>
																	</span>
																	{size !=
																		'Small' &&
																		(resource
																			?.link
																			?.label ||
																			resource
																				?.link
																				?.src) && (
																			<span className="d-none d-md-block ms-2">
																				{resource
																					?.link
																					?.label ||
																					resource?.fileLabel ||
																					t(
																						'Download',
																					)}
																			</span>
																		)}
																</a>
															</div>
														)}
													</div>
												</div>
											)}
											{style === 'List' && (
												<a
													href={
														resource?.link?.src ||
														'#generated-automatically-for-front-end'
													}
													target="_blank"
													rel="noreferrer noopener"
													className="d-flex align-items-center gap-4 p-3 px-md-5 py-3 pe-3 text-decoration-none"
													key={index}
												>
													<div className="d-flex align-items-center">
														{resource.heading && (
															<span className="calian-resource-download__list-heading">
																{
																	resource.heading
																}
															</span>
														)}
													</div>
													<div className="ms-auto d-flex align-items-center">
														{resource?.link
															?.prettySize && (
															<span className="calian-resource-download__list-size me-2">
																{
																	resource
																		?.link
																		?.prettySize
																}
															</span>
														)}
														<span className="calian-resource-download__list-icon">
															<Icon
																name={
																	'download'
																}
															/>
														</span>
													</div>
												</a>
											)}
										</div>
									),
							)}
					</div>
					{style === 'CTA' &&
						size != 'Small' &&
						enableLineAnimations && (
							<AnimatedArcs
								arcsStyle={'Component - Two Lines'}
								arcsPosition={'Right'}
								arcOneColor={
									backgroundColor == 'Dark'
										? 'var(--bs-primary-mid)'
										: 'var(--bs-light)'
								}
								arcOneStrokeWidth={4}
								arcTwoColor={
									backgroundColor == 'Dark'
										? 'var(--bs-primary)'
										: 'var(--bs-white)'
								}
								arcTwoStrokeWidth={3}
								animationDelay={250}
								animationOffset={150}
							/>
						)}
				</div>
			</div>
		</div>
	);
};
export default ResourceDownload;
